import React from 'react';

import {DashboardPage} from 'app/components/general/DashboardPage';
import {BreadcrumbItemStructure} from 'app/general/structures/BreadcrumbItemStructure';
import {useTags} from 'app/tags/api';
import {selectTagFilters} from 'app/tags/slice';
import {TAGS_DASHBOARD_URL, TAGS_QRCODE_URL} from 'app/urls';
import {DEFAULT_PAGE_SIZE} from 'app/utils/constants';
import {getContentType} from 'app/utils/content-type';
import {gt, ngt} from 'app/utils/i18n';

import {CompactTagListItem} from './CompactTagListItem';
import {TagsFilterbar} from './TagsFilterbar';

import {TagSerializedType} from 'sweet-types';

import type {BreadcrumbProps} from 'app/general/structures/BreadcrumbItemStructure';
import type {
  TagsDashboardFetchParamsType,
  TagsDashboardLocationParamsType,
} from 'app/tags/types';
import type {WithRouterProps} from 'react-router';

export const TagsDashboard = (routerProps: WithRouterProps) => {
  return (
    <DashboardPage<
      TagSerializedType,
      TagsDashboardLocationParamsType,
      TagsDashboardFetchParamsType
    >
      {...routerProps}
      title={gt('Tags')}
      actionArea={
        <a
          href={TAGS_QRCODE_URL}
          rel="noopener noreferrer"
          target="_blank"
          className="btn btn-sm btn-light"
        >
          <i className="fa fa-fw fa-qrcode" />{' '}
          <span className="d-none d-md-inline">{gt('Show QR Codes')}</span>
        </a>
      }
      useDataHook={useTags}
      filterbar={({resetFilters, viewingAll}) => (
        <TagsFilterbar resetFilters={resetFilters} viewingAll={viewingAll} />
      )}
      filtersSelector={selectTagFilters}
      convertParamsFunc={convertLocationParamsToFetchParams}
      renderItem={(tag) => <CompactTagListItem key={tag.id} tag={tag} />}
      messages={{
        counter: (count) => ngt('%s tag', '%s tags', count),
        notFound: ({viewingAll, resetFilters}, NoResultsMessage) => (
          <NoResultsMessage
            head={
              viewingAll
                ? gt('No Tags on your account')
                : gt('No Tags found with these filters')
            }
          >
            {viewingAll ? null : (
              <span>
                <button
                  className="btn-outline-secondary btn btn-sm"
                  onClick={resetFilters}
                >
                  <i className="fa fa-times" />{' '}
                  {gt('Reset filters and view all Tags')}
                </button>
              </span>
            )}
          </NoResultsMessage>
        ),
      }}
      onModalChangeMessage={({refetch}) =>
        (message, {runWhenAny, anyModelMatch}) => {
          runWhenAny(message, [anyModelMatch(['tag'])], refetch);
        }}
    />
  );
};

const tagCt = getContentType('tag');

export const TagsDashboardBreadcrumb = (props: BreadcrumbProps) => (
  <BreadcrumbItemStructure
    {...props}
    icon={tagCt.icon}
    text={gt('Tags')}
    link={TAGS_DASHBOARD_URL}
  />
);

export const convertLocationParamsToFetchParams = (
  query: TagsDashboardLocationParamsType
): TagsDashboardFetchParamsType => ({
  page: query.page != null ? query.page : '1',
  page_size:
    query.page_size != null ? query.page_size : DEFAULT_PAGE_SIZE.toString(),
  search: query.search,
  slug: query.slug,
  exclude_slug: query.exclude_slug,
});
